import React from 'react';
import PropTypes from 'prop-types';
import { formatMoneyForLocale, useI18n } from '@stitch-fix/i18n';
import { Box, Inline, Stack, Text, Link } from '@stitch-fix/mode-react';
import './styles.module.scss';
import cx from 'classnames';
import { LIMITED_STOCK, OUT_OF_STOCK } from '../../../helpers/availability';
import StockStatus from '../../StockStatus';
import QuantitySelector from './QuantitySelector';
import trackClickIntoPDP from './BagItem.tracking';

const BagItem = ({
  product,
  quantity,
  subSource,
  incrementQuantity,
  decrementQuantity,
  hidden,
  remove,
  showStockStatus,
}) => {
  const { locale } = useI18n();
  const { brand, name, variantBySkuId: sku } = product;
  const { image, inventory, size, price, color, url } = sku;

  const outOfStock = inventory.availability === OUT_OF_STOCK;
  const limitedStock = inventory.availability === LIMITED_STOCK;
  const stockMessage = inventory.stockMessage;

  const buildPDPPath = () => {
    if (!subSource) {
      return url;
    }

    const pdpUrl = new URL(url);

    pdpUrl.searchParams.append('sub_source', subSource);

    return pdpUrl;
  };

  const imgBorderRadius = '0.5rem';

  return (
    <Box styleName={`bag-item ${hidden ? 'hidden' : ''}`} width="100%">
      <Box display="flex">
        <Box
          mr={1}
          bgColor="gray-95"
          width="100%"
          maxWidth="104px"
          height="130px"
          data-testid="item-image"
          style={{ borderRadius: imgBorderRadius }}
        >
          <a
            href={buildPDPPath()}
            onClick={() => trackClickIntoPDP(sku, subSource)}
            data-testid="pdp-image"
          >
            <img
              alt={image.altText}
              src={image.transformedSrc}
              height="100%"
              width="100%"
              style={{ objectFit: 'contain', borderRadius: imgBorderRadius }}
            />
          </a>
        </Box>
        <Box styleName="bag-item-content">
          <Text
            height="short"
            family="medium"
            scale="1"
            color={outOfStock ? 'blue-45' : 'gray-16'}
          >
            <div styleName="product-details">
              <Stack spacing={0.5} hAlign="stretch">
                <Text style={{ textTransform: 'uppercase' }}>{brand.name}</Text>
                <Text height="short" family="regular" scale="3">
                  <a
                    href={buildPDPPath()}
                    onClick={() => trackClickIntoPDP(sku, subSource)}
                  >
                    {name}
                  </a>
                </Text>
                <Text style={{ textDecoration: outOfStock && 'line-through' }}>
                  {formatMoneyForLocale(locale, price, { subunits: true })}
                </Text>
                <Text
                  height="short"
                  family="regular"
                  scale="2"
                  color={outOfStock ? 'gray-45' : 'gray-35'}
                >
                  <Inline spacing={0.5} vAlign="center">
                    {size.name}
                    <Box width="1px" height={1} bgColor="gray-85" />
                    <Text style={{ textTransform: 'capitalize' }}>
                      {color.name}
                    </Text>
                  </Inline>
                </Text>
                {(outOfStock || limitedStock) && showStockStatus && (
                  <StockStatus
                    outOfStock={outOfStock}
                    stockMessage={stockMessage}
                    inShoppingBag
                  />
                )}
              </Stack>
            </div>
          </Text>
        </Box>
      </Box>
      <Box
        styleName={cx('bag-item-actions', { 'out-of-stock-item': outOfStock })}
      >
        {!outOfStock && (
          <Box data-testid="quantity-selector">
            <QuantitySelector
              skuId={sku.id}
              quantity={quantity}
              increment={incrementQuantity}
              decrement={decrementQuantity}
              quantityAvailable={inventory.quantityAvailable}
            />
          </Box>
        )}
        <Text color="gray-35" my={0.5}>
          <Link
            as="button"
            variant="inherit"
            setting="body-small"
            onClick={remove}
          >
            Remove
          </Link>
        </Text>
      </Box>
    </Box>
  );
};

BagItem.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    brand: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    variantBySkuId: PropTypes.shape({
      color: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
      image: PropTypes.shape({
        altText: PropTypes.string.isRequired,
        transformedSrc: PropTypes.string.isRequired,
      }).isRequired,
      id: PropTypes.string.isRequired,
      inventory: PropTypes.shape({
        availability: PropTypes.string.isRequired,
        quantityAvailable: PropTypes.number.isRequired,
        stockMessage: PropTypes.shape({
          heading: PropTypes.string,
          subheading: PropTypes.string,
        }),
      }),
      price: PropTypes.shape({
        currency: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }).isRequired,
      size: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  quantity: PropTypes.number.isRequired,
  subSource: PropTypes.string,
  incrementQuantity: PropTypes.func.isRequired,
  decrementQuantity: PropTypes.func.isRequired,
  remove: PropTypes.func.isRequired,
  hidden: PropTypes.bool,
  showStockStatus: PropTypes.bool,
};

BagItem.defaultProps = {
  subSource: '',
  hidden: false,
  showStockStatus: true,
};

export default BagItem;
